'use client';

import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';
import React, { useCallback, useState } from 'react';
import { RuntimeConfiguration } from '@/configuration';
import CoinFlowLayout from '@/layouts/CoinflowLayout';
import { useOpenClaimModal } from '@/provider/ClaimModalOpenProvider';
import { trackGAEvent } from '@/utils/googleAnalytics';
import { WalletProviderType, emailRegex } from '../constants';
import {
  ClaimContainerType,
  ClaimTokenModalProps,
  SaleType,
  SocialAuthOption,
  SupportedNetworksData
} from '../interface';
import ClaimModalLayout from '../layout/ClaimModalLayout';
import { useAppKitModalContent } from '../providers/AppKitModalProvider';
import { useMetaMask } from '../providers/MetaMaskWalletProvider';
import { useModalState } from '../providers/ModalStateProvider';
import { usePayment } from '../providers/PaymentProvider';
import { WalletDetailsProps, useWalletDetails } from '../providers/WalletDetailsProvider';
import { useWalletOptions } from '../providers/WalletOptionProvider';
import { StorageService } from '../services';
import { handleErrorMessages } from '../utils/errorMessages.utils';
import { getBalance } from '../utils/getBalance.utils';
import getMetaMaskBalance from '../utils/getMetaMaskWalletBalance';
import getNetworkDetailsByChainId from '../utils/getNetworkDetailsByChainId';
import { DEFAULT_WALLET_DETAILS } from '../constants/walletProvider';

type SaveSessionWithUserDataProps = {
  walletAddress: string;
  networkDetails: SupportedNetworksData;
  balance: {
    native: number;
    nonNative: number;
  };
  walletType: WalletProviderType;
  provider: ethers.providers.Web3Provider | null;
};

type SignWithLoginMojitoAPI = Omit<SaveSessionWithUserDataProps, 'networkDetails'> & {
  walletType: WalletProviderType;
  isSign: boolean;
  walletAddress: string;
  chainId: string;
};

const networkDetailsData = [
  {
    id: 'c53715d0-7b59-44a8-8400-f9f668b5061d',
    name: 'Ethereum Mainnet',
    chainID: 1,
    isTestnet: false,
    __typename: 'Network'
  },
  {
    id: '9ca86d5b-60a5-416f-ac37-7b209653dbed',
    name: 'Sepolia Ethereum Testnet',
    chainID: 11155111,
    isTestnet: true,
    __typename: 'Network'
  }
];

const ClaimTokenModal = ({
  open,
  config,
  userEmail,
  link,
  content,
  tokenName,
  showDisconnectButton,
  showBuyButton = true,
  claimType,
  skipAPI,
  onClickBuyToken,
  onCloseModal,
  onSuccess
}: ClaimTokenModalProps) => {
  const { isDisconnect, setIsDisconnect } = useOpenClaimModal();
  const { walletDetails, setWalletDetails } = useWalletDetails();

  const {
    metaMaskSdk,
    onDisconnect: onMetaMaskDisconnect,
    getConnectedAccounts,
    getConnectedChainId,
    onConnectMetaMask,
    onSignMetaMask,
    onSwitchMetaMaskNetwork,
    getMetaMaskProvider
  } = useMetaMask();

  const {
    connectedWalletAddress,
    walletProvider,
    onConnectWallet,
    getConnectedChainId: getConnectedAppKitChainID,
    onDisconnect: onWalletDisconnect,
    onSignPersonalMessage,
    onConnectWalletAddress,
    onSwitchConfigNetwork
  } = useAppKitModalContent();

  const {
    error: paymentError,
    isLoading: paymentLoading,
    tokenGatingError: paymentGatingError,
    validation: paymentValidation,
    priceInEth,
    loaderTitle: paymentLoaderTitle,
    onInitiateBuyNow,
    setValidation: setPaymentValidation,
    onClickSkipPaymentMethod,
    onContinuePayment,
    onRetry
  } = usePayment();

  const { modalState, setModalState } = useModalState();
  const walletOptions = useWalletOptions();
  const [discountCode, setDiscountCode] = useState<string>('');
  const [error, setError] = useState('');
  const [inValidCode, setInValidCode] = useState<string | null>();
  const [email, setEmail] = useState<string>(userEmail ?? '');
  const [otp, setOTP] = useState<string>('');
  const [loaderTitle, setLoaderTitle] = useState<string>('');
  const [recoveryCode, setRecoveryCode] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const [isOAuth, setIsOAuth] = useState<boolean>(false);
  const [loaderDescription, setLoaderDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<'clear' | 'no_balance' | 'wrong_network'>('clear');
  const [openCoinFlow, setIsOpenCoinFlow] = useState<boolean>(false);

  const [coinFlowData] = useState({
    price: 0,
    encodeData: ''
  });

  const onDisConnect = useCallback(async () => {
    setInValidCode(null);
    setDiscountCode('');
    setLoaderTitle('');
    setError('');
    setIsLoading(false);
    setEmail('');
    await onWalletDisconnect();
    await onMetaMaskDisconnect();
    // onDisconnectThirdWebWallet();
    StorageService.walletDetails.clear();
    StorageService.token.clear();
    StorageService.tokenType.clear();
    StorageService.metaData.clear();
    StorageService.transactionHash.clear();
    StorageService.accessToken.clear();
    setWalletDetails(DEFAULT_WALLET_DETAILS);
    setModalState(ClaimContainerType.EXISTING_WALLET);
    setIsDisconnect(false);
  }, [
    onWalletDisconnect,
    onMetaMaskDisconnect,
    // onDisconnectThirdWebWallet,
    setWalletDetails,
    setModalState,
    setIsDisconnect
  ]);

  const verifyOTP = useCallback(
    async (otpValue: string) => {
      try {
        setLoaderTitle('Processing...');
        setModalState(ClaimContainerType.LOADING);
        // await onEmailWithOtp(email, otpValue);
      } catch (e: any) {
        setInValidCode(null);
        setDiscountCode('');
        setError('Code is incorrect');
        setModalState(ClaimContainerType.OTP);
      }
    },
    [setModalState]
  );

  const onSaveSessionWithUserData = useCallback(
    ({ walletAddress, balance, walletType, networkDetails }: SaveSessionWithUserDataProps) => {
      const walletData: WalletDetailsProps = {
        address: walletAddress,
        networkDetails,
        provider: null,
        refetchBalance: false,
        providerType: walletType,
        balance: balance ?? {
          native: 0,
          nonNative: 0
        }
      };

      StorageService.walletDetails.setValue(walletData);
      trackGAEvent('Connect_Wallet_Success', {
        type: walletType,
        walletAddress,
        balance: balance?.native
      });
    },
    []
  );

  const onSignWithLogin = useCallback(
    async ({ provider, walletType, balance, isSign, walletAddress, chainId }: SignWithLoginMojitoAPI) => {
      try {
        const networkDetails = getNetworkDetailsByChainId(networkDetailsData, +chainId);

        if (!networkDetails) {
          throw new Error('Network details not found for the provided chainId');
        }

        const timestamp = new Date().toISOString();

        // Construct message
        const signMessage = `This message is used for signature validation for organization kinetek and wallet address ${walletAddress} at ${timestamp}`;

        let signature: string | undefined | null;

        if (isSign && signMessage) {
          switch (walletType) {
            case WalletProviderType.METAMASK:
              signature = await onSignMetaMask(signMessage);
              break;
            case WalletProviderType.WALLET_CONNECT:
              signature = await onSignPersonalMessage(signMessage);
              break;
            default:
              throw new Error(`Unsupported wallet type: ${walletType}`);
          }
        }

        if (signature && signMessage) {
          StorageService.token.setValue(signature);
          StorageService.tokenType.setValue(chainId);
          StorageService.loginTime.setValue(Date.now());
        }

        onSaveSessionWithUserData({
          walletAddress,
          networkDetails,
          provider,
          balance,
          walletType
        });
      } catch (e) {
        throw e;
      }
    },
    [onSignPersonalMessage, onSaveSessionWithUserData, onSignMetaMask]
  );

  const onChangeCode = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setDiscountCode(event.target.value);
  }, []);

  const onChangeEmail = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setEmail(event.target.value);
  }, []);

  const onChangeOTP = useCallback(
    (event: string) => {
      setError('');
      setOTP(event);
      if (event.length === 6) {
        verifyOTP(event);
      }
    },
    [verifyOTP]
  );

  const onChangeRecoveryCode = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setRecoveryCode(event.target.value);
  }, []);

  const onChangeCheck = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setChecked(event.target.checked);
  }, []);

  const onClickMetamask = useCallback(async () => {
    try {
      trackGAEvent('Connect_Wallet', {
        type: WalletProviderType.METAMASK
      });
      setLoaderTitle('Connecting wallet...');
      setModalState(ClaimContainerType.LOADING);
      setLoaderDescription('Please open your wallet and confirm the signature. Do not close or reload this window.');
      const isAccount = async () => {
        const accounts = await getConnectedAccounts();

        return !accounts || accounts.length !== 0;
      };
      const accountStatus = await isAccount();

      if (!accountStatus) {
        await onConnectMetaMask();
      }
      const initialChain = await getConnectedChainId();

      if (initialChain !== config?.chainId) {
        setLoaderDescription(
          'You are in the wrong network. To switch it, open your wallet and confirm. Please do not close or reload this window.'
        );
        const hexValue = parseInt(String(config?.chainId), 10).toString(16);
        const chain = hexValue.padStart(6, '');

        await onSwitchMetaMaskNetwork(`0x${chain}`);
      }
      setLoaderDescription('Please open your wallet and confirm the signature. Do not close or reload this window.');
      const accounts = await getConnectedAccounts();
      const chainId = await getConnectedChainId();
      const balance = await getMetaMaskBalance(metaMaskSdk, accounts?.[0], chainId ?? 11155111);
      const provider = getMetaMaskProvider();

      if ((claimType === SaleType.NoCode || claimType === SaleType.BuyNow) && skipAPI && accounts?.[0] && chainId) {
        if (claimType === SaleType.BuyNow) {
          await onSignWithLogin({
            balance,
            chainId: String(chainId),
            provider: provider ?? null,
            isSign: true,
            walletAddress: accounts?.[0],
            walletType: WalletProviderType.METAMASK
          });
          onCloseModal();
        }
      } else {
        setLoaderTitle('');
        setLoaderDescription('');
        setInValidCode(null);
        setDiscountCode('');
        setModalState(ClaimContainerType.ERROR);
        setError(
          'Please download MetaMask first. MetaMask is a browser extension to access and manage your digital objects.'
        );
      }
    } catch (e: any) {
      setInValidCode(null);
      setDiscountCode('');
      if (e?.message?.toLowerCase()?.includes('user rejected') || e?.message?.toLowerCase()?.includes('user denied')) {
        setModalState(ClaimContainerType.ERROR);
        setError('Signature requests rejected. Please choose "Retry" and confirm to proceed.');
      } else if (e?.message?.toLowerCase()?.includes('already processing')) {
        setModalState(ClaimContainerType.ERROR);
        setError('Already processing requested accounts. Please wait.');
      } else {
        setModalState(ClaimContainerType.ERROR);
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoaderTitle('');
      setLoaderDescription('');
    }
  }, [
    setModalState,
    getConnectedChainId,
    config?.chainId,
    getConnectedAccounts,
    metaMaskSdk,
    claimType,
    skipAPI,
    onSignWithLogin,
    onConnectMetaMask,
    onSwitchMetaMaskNetwork,
    getMetaMaskProvider,
    onCloseModal
  ]);

  const onWalletConnect = useCallback(
    async (provider: any) => {
      try {
        const connectedChainId = await getConnectedAppKitChainID();
        setLoaderTitle('Connecting wallet...');
        setModalState(ClaimContainerType.LOADING);
        const connectedAddress = await onConnectWalletAddress();
        if (!connectedAddress) return;
        if (+RuntimeConfiguration.CHAIN_ID !== connectedChainId) {
          try {
            setLoaderDescription(
              'Invalid network detected. Kindly open your wallet and confirm the network change. Do not close or reload this window.'
            );
            await onSwitchConfigNetwork(String(config?.chainId));
          } catch {
            throw new Error(
              'The current network is not supported. Please switch to the appropriate network and try again.'
            );
          }
        }
        setLoaderDescription('Please open your wallet and confirm the signature. Do not close or reload this window.');
        const ethersProvider = new ethers.providers.Web3Provider(provider);
        const chainId = await getConnectedAppKitChainID();
        const balance = await getBalance(ethersProvider, connectedAddress, chainId, WalletProviderType.WALLET_CONNECT);

        if ((claimType === SaleType.NoCode || claimType === SaleType.BuyNow) && skipAPI) {
          if (claimType === SaleType.BuyNow) {
            await onSignWithLogin({
              balance,
              chainId: String(chainId),
              provider: ethersProvider,
              isSign: true,
              walletAddress: connectedAddress,
              walletType: WalletProviderType.WALLET_CONNECT
            });
            onCloseModal();
          }

          return;
        }
      } catch (e: any) {
        const errorMsg = e?.message?.toLowerCase();
        setInValidCode(null);
        setDiscountCode('');

        if (errorMsg?.includes('user rejected') || errorMsg?.includes('user denied')) {
          setModalState(ClaimContainerType.ERROR);
          setError('Signature request was rejected. Please choose "Retry" and confirm to proceed.');
          return;
        }
        if (errorMsg?.includes('sign failed. please try again.')) {
          setModalState(ClaimContainerType.ERROR);
          setError('Signature request failed. Please choose "Retry" and confirm to proceed.');
          return;
        }
        if (errorMsg?.includes('current network is not supported')) {
          setModalState(ClaimContainerType.ERROR);
          setError(`The current network is not supported. Please switch to the appropriate network and try again.`);
          return;
        }

        setModalState(ClaimContainerType.ERROR);
        setError('Something went wrong. Please try again.');
      } finally {
        setLoaderTitle('');
        setLoaderDescription('');
      }
    },
    [
      getConnectedAppKitChainID,
      onConnectWalletAddress,
      setModalState,
      claimType,
      skipAPI,
      onSwitchConfigNetwork,
      config?.chainId,
      onSignWithLogin,
      onCloseModal
    ]
  );

  const onClickWalletConnect = useCallback(async () => {
    setError('');
    try {
      //INFO: It used to make the wallet connect search is clickable.
      if (typeof window !== 'undefined') {
        const modalContainer = document.querySelector('#wallet-connect-modal .MuiDialog-container[tabindex="-1"]');

        if (modalContainer) {
          modalContainer.removeAttribute('tabindex');
        }
      }

      trackGAEvent('Connect_Wallet', {
        type: WalletProviderType.WALLET_CONNECT
      });

      await onConnectWallet();
    } catch (e: any) {
      setLoaderTitle('');
      setInValidCode(null);
      setDiscountCode('');
      setModalState(ClaimContainerType.ERROR);
      setError('Something went wrong. Please try again.');
    }
  }, [onConnectWallet, setModalState]);

  const onClickContinueWithSocial = useCallback(
    async (type: SocialAuthOption) => {
      try {
        trackGAEvent('Connect_Wallet', {
          type: WalletProviderType.EMAIL
        });
        setLoaderTitle('Processing...');
        setModalState(ClaimContainerType.LOADING);
        // await onThirdWebSocialLogin(type);
      } catch (e: any) {
        const message = e?.message?.toLowerCase() || '';

        if (message.includes('opening pop-up')) {
          // await onThirdWebSocialLogin(type, { auth: { options: [type], mode: 'redirect' } });
        } else {
          setLoaderTitle('');
          setInValidCode(null);
          setDiscountCode('');
          setModalState(ClaimContainerType.ERROR);
          const errorNote = handleErrorMessages(e);

          setError(errorNote);
        }
      }
    },
    [setModalState]
  );

  useQuery({
    queryKey: [
      'reown_wallet_connect',
      {
        walletConnectProvider: !!walletProvider
      }
    ],
    queryFn: async () => {
      //To avoid sign once the wallet is connected.
      const wallets = StorageService.walletDetails.getValue() as WalletDetailsProps;
      if (!!connectedWalletAddress && !!walletProvider && !wallets?.address) {
        await onWalletConnect(walletProvider);
      }

      return null;
    },
    enabled: !!connectedWalletAddress && !!walletProvider && !walletDetails?.provider
  });

  useQuery({
    queryKey: ['wallet_disconnect', { isDisconnect }],
    queryFn: async () => {
      await onDisConnect();
      console.log('onDisConnect: ', isDisconnect);

      return null;
    },
    enabled: isDisconnect
  });

  const onClickContinueWithEmail = useCallback(async () => {
    setLoaderDescription('');
    try {
      setOTP('');
      if (!email) {
        setError('Email is required');

        return;
      }
      if (emailRegex.test(email)) {
        trackGAEvent('Connect_Wallet', {
          type: WalletProviderType.EMAIL
        });
        setError('');
        setLoaderTitle('Processing...');
        setModalState(ClaimContainerType.LOADING);
        // await onContinueWithThirdWebEmail(email);
        setModalState(ClaimContainerType.OTP);
      } else {
        setError('Please enter a valid email');
      }
    } catch (e: any) {
      setLoaderTitle('');
      setInValidCode(null);
      setDiscountCode('');
      setModalState(ClaimContainerType.OTP);
      setError(e?.message);
    }
  }, [email, setModalState]);

  const onClickExistingWallet = useCallback(() => {
    setModalState(ClaimContainerType.EXISTING_WALLET);
  }, [setModalState]);

  const onClickRecoveryCode = useCallback(() => {
    if (!recoveryCode) {
      setError('Recovery code is required');

      return;
    }
    setModalState(ClaimContainerType.OTP);
  }, [recoveryCode, setModalState]);

  const onClickBreadcrumb = useCallback(
    (e: string) => {
      if (ClaimContainerType.EXISTING_WALLET === e && walletOptions.enableEmailWallet) {
        setModalState(ClaimContainerType.CONNECT_WALLET);
      }
    },
    [walletOptions, setModalState]
  );

  const onClickDoNotHaveWallet = useCallback(() => {
    setModalState(ClaimContainerType.CONNECT_WALLET);
  }, [setModalState]);

  const afterSwitch = useCallback(
    async (switchChainId: number) => {
      if (walletProvider) {
        const provider = new ethers.providers.Web3Provider(walletProvider);
        const balanceData = await getBalance(
          provider,
          walletDetails.address,
          switchChainId,
          walletDetails.providerType
        );
        const walletData: WalletDetailsProps = {
          address: walletDetails.address,
          networkDetails: walletDetails?.networkDetails,
          provider: null,
          refetchBalance: false,
          providerType: walletDetails.providerType,
          balance: balanceData ?? {
            native: 0,
            nonNative: 0
          }
        };

        StorageService.walletDetails.setValue(walletData);
        setLoaderTitle('');
        setLoaderDescription('');
        setModalState(ClaimContainerType.PAY_WITH_GAS);
        setValidation('clear');
      }
    },
    [walletDetails, walletProvider, setModalState]
  );

  const onClickSwitchNetwork = useCallback(
    async (chainId: number) => {
      try {
        setLoaderTitle('Connecting wallet...');
        setLoaderDescription(
          'Invalid network detected. Kindly open your wallet and confirm the network change. Do not close or reload this window.'
        );
        setModalState(ClaimContainerType.LOADING);
        const hexValue = parseInt(String(chainId), 10).toString(16);
        const chain = hexValue.padStart(6, '');

        if (walletDetails?.providerType === WalletProviderType?.METAMASK) {
          await onSwitchMetaMaskNetwork(`0x${chain}`);
          await onClickMetamask();

          return;
        }
        if (walletDetails?.providerType === WalletProviderType?.WALLET_CONNECT && walletDetails.provider) {
          await walletDetails.provider.send('wallet_switchEthereumChain', [{ chainId: `0x${chain}` }]);
          setTimeout(() => {
            afterSwitch(chainId);
          }, 2000);

          return;
        }
        // await switchChain(defineChain(Number(chainId)));
      } catch (e: any) {
        setLoaderTitle('');
        setLoaderDescription('');
        setModalState(ClaimContainerType.ERROR);
        if (e?.message === 'User rejected the request.') {
          setError('Network switch has been cancelled by user');

          return;
        }
        setError('Something went wrong. Please try again.');
      }
    },
    [
      setModalState,
      walletDetails?.providerType,
      walletDetails.provider,
      onSwitchMetaMaskNetwork,
      onClickMetamask,
      afterSwitch
    ]
  );

  const onInitiateSkipAPI = useCallback(async () => {
    setLoaderDescription('');
    await onInitiateBuyNow(onSuccess);
  }, [onInitiateBuyNow, onSuccess]);

  const onCloseCreditCard = useCallback(() => {
    setIsOpenCoinFlow(false);
  }, []);

  const handleCloseModal = useCallback(
    (event: ClaimContainerType) => {
      setPaymentValidation('clear');
      onCloseModal();
      setIsOAuth(false);
      if (event === ClaimContainerType.PAY_WITH_GAS) {
        setModalState(ClaimContainerType.PAY_WITH_GAS);
      }
      if (event === ClaimContainerType.OTP) {
        setModalState(ClaimContainerType.CONNECT_WALLET);
      }
    },
    [setPaymentValidation, onCloseModal, setModalState]
  );

  const handleRetry = useCallback(() => {
    setError('');
    setEmail('');
    setPaymentValidation('clear');
    onRetry();
  }, [onRetry, setPaymentValidation]);

  return (
    <>
      <ClaimModalLayout
        open={open || isOAuth}
        claimStatus=""
        currentModalState={modalState}
        code={discountCode}
        error={error || paymentError}
        inValidCode={inValidCode}
        address={walletDetails?.address}
        isEnterClaimCode={claimType === SaleType.CustomCode}
        email={email}
        otp={otp}
        loaderTitle={content?.LoaderContent?.title || loaderTitle || paymentLoaderTitle}
        recoveryCode={recoveryCode}
        walletOptions={walletOptions}
        logoUrl={link?.logoUrl}
        checked={checked}
        additionalTermsUrl={link?.additionalTermsUrl}
        termsUrl={link?.termsUrl}
        privacyUrl={link?.privacyUrl}
        content={content}
        viewTokenTrackerURL={link?.viewTokenTrackerURL ?? true}
        tokenTrackerURL=""
        tokenName={tokenName}
        loaderDescription={loaderDescription}
        tokenGatingName=""
        tokenGatingError={paymentGatingError}
        showDisconnectButton={showDisconnectButton}
        showBuyButton={showBuyButton}
        isTokenGating={claimType === SaleType.TokenGating || claimType === SaleType.SkipTokenGating || skipAPI}
        price={priceInEth}
        showLoader={isLoading || paymentLoading}
        validation={paymentValidation || validation}
        currency={
          walletDetails?.networkDetails?.chainID === 137 || walletDetails?.networkDetails?.chainID === 80001
            ? 'MATIC'
            : 'ETH'
        }
        networkName={walletDetails?.networkDetails?.name}
        onClickDoNotHaveWallet={onClickDoNotHaveWallet}
        onChangeCheck={onChangeCheck}
        onChangeCode={onChangeCode}
        onChangeEmail={onChangeEmail}
        onChangeRecoveryCode={onChangeRecoveryCode}
        onChangeOTP={onChangeOTP}
        onClickMetamask={onClickMetamask}
        onClickWalletConnect={onClickWalletConnect}
        onClickContinueWithEmail={onClickContinueWithEmail}
        onClickExistingWallet={onClickExistingWallet}
        onClickRecoveryCode={onClickRecoveryCode}
        onCloseModal={handleCloseModal}
        handleSuccessRedirection={onSuccess}
        handleRetry={handleRetry}
        onClickBreadcrumb={onClickBreadcrumb}
        onClickContinueCheckToken={() => onContinuePayment(config?.chainId)}
        onClickBuyToken={onClickBuyToken}
        onClickDisconnectWallet={onDisConnect}
        onClickClaimWithGas={onInitiateSkipAPI}
        onClickSwitchNetwork={() => onClickSwitchNetwork(config?.chainId)}
        onClickContinueWithSocial={onClickContinueWithSocial}
        onClickSkip={() => onClickSkipPaymentMethod(config?.chainId)}
      />
      {openCoinFlow && <CoinFlowLayout data={coinFlowData} open={openCoinFlow} handleClose={onCloseCreditCard} />}
    </>
  );
};

export default ClaimTokenModal;
